import React, { useState } from "react";
import { toast } from "sonner";
import { StepShell, Field, TInput, TArea, SelectNative, Chips } from "./WizardUI";
import { GENRES, STATION_TYPES, LANGUAGES, COUNTRIES, TIMEZONES } from "./constants";

export default function Step2Station({ saved, station, onSave, onBack, busy }) {
  const init = { ...(station || {}), ...(saved || {}) };
  const initPrimary = (init.primary_formats && init.primary_formats.length
    ? init.primary_formats
    : (init.primary_format || init.genre || "").split(" / ").filter(Boolean)).slice(0, 2);
  const [f, setF] = useState({
    name: init.name || "", tagline: init.tagline || "", website: init.website || "",
    country: init.country || "", timezone: init.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    description: init.description || "", primary_formats: initPrimary,
    secondary_formats: init.secondary_formats || [], target_audience: init.target_audience || "",
    broadcast_language: init.broadcast_language || "English", station_type: init.station_type || "internet",
  });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  const setPrimary = (vals) => {
    if (vals.length > 2) { toast.error("Pick up to two primary formats"); return; }
    setF((s) => ({ ...s, primary_formats: vals, secondary_formats: s.secondary_formats.filter((g) => !vals.includes(g)) }));
  };

  const submit = () => {
    if (!f.name.trim()) { toast.error("Station name is required"); return; }
    if (!f.primary_formats.length) { toast.error("Pick at least one primary music format"); return; }
    if (f.website && !/^https?:\/\/\S+\.\S{2,}/i.test(f.website)) { toast.error("Website must be a valid URL (https://…)"); return; }
    onSave(f);
  };

  return (
    <StepShell step={2} title="Tell us about your station"
      subtitle="This powers everything the AI writes for you — the more accurate, the better the content."
      onBack={onBack} onContinue={submit} busy={busy} canContinue={!!f.name.trim()}>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Station name" required testid="onb-station-name">
          <TInput value={f.name} onChange={(e) => set("name")(e.target.value)} placeholder="e.g. Seductive Vibes Radio" data-testid="onb-station-name-input" />
        </Field>
        <Field label="Slogan / tagline" testid="onb-station-tagline">
          <TInput value={f.tagline} onChange={(e) => set("tagline")(e.target.value)} placeholder="e.g. Smooth sounds all night long" />
        </Field>
        <Field label="Website address" testid="onb-station-website">
          <TInput value={f.website} onChange={(e) => set("website")(e.target.value)} placeholder="https://yourstation.com" />
        </Field>
        <Field label="Country">
          <SelectNative options={COUNTRIES} value={f.country} onChange={set("country")} placeholder="Choose country…" testid="onb-station-country" />
        </Field>
        <Field label="Time zone">
          <SelectNative options={TIMEZONES} value={f.timezone} onChange={set("timezone")} testid="onb-station-timezone" />
        </Field>
        <Field label="Broadcasting language">
          <SelectNative options={LANGUAGES} value={f.broadcast_language} onChange={set("broadcast_language")} testid="onb-station-language" />
        </Field>
      </div>
      <Field label="Station description">
        <TArea value={f.description} onChange={(e) => set("description")(e.target.value)}
          placeholder="What makes your station special? Who listens, what do you play, what's the vibe?" data-testid="onb-station-desc" />
      </Field>
      <Field label="Primary music format" required hint="Pick one or two.">
        <Chips options={GENRES} values={f.primary_formats}
          onChange={setPrimary} testidPrefix="onb-primary" />
      </Field>
      <Field label="Secondary music formats" hint="Pick as many as apply.">
        <Chips options={GENRES.filter((g) => !f.primary_formats.includes(g))} values={f.secondary_formats}
          onChange={set("secondary_formats")} testidPrefix="onb-secondary" />
      </Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Target audience">
          <TInput value={f.target_audience} onChange={(e) => set("target_audience")(e.target.value)}
            placeholder="e.g. Adults 25-54 who love classic slow jams" data-testid="onb-station-audience" />
        </Field>
        <Field label="Station type">
          <SelectNative options={STATION_TYPES} value={f.station_type} onChange={set("station_type")} testid="onb-station-type" />
        </Field>
      </div>
    </StepShell>
  );
}
